import { ErrorHandler, Injectable } from '@angular/core';
import { LatLng, MapsAPILoader } from '@agm/core';
import {} from 'googlemaps';
import AutocompleteService = google.maps.places.AutocompleteService;
import AutocompletionRequest = google.maps.places.AutocompletionRequest;
import AutocompletePrediction = google.maps.places.AutocompletePrediction;
import QueryAutocompletePrediction = google.maps.places.QueryAutocompletePrediction;
import QueryAutocompletionRequest = google.maps.places.QueryAutocompletionRequest;
import Geocoder = google.maps.Geocoder;
import GeocoderRequest = google.maps.GeocoderRequest;
import GeocoderResult = google.maps.GeocoderResult;
import PlaceResult = google.maps.places.PlaceResult;
import PlaceSearchRequest = google.maps.places.PlaceSearchRequest;
import PlacesService = google.maps.places.PlacesService;
import PlaceDetailsRequest = google.maps.places.PlaceDetailsRequest;

import { LatLon } from '../util/geodesy/latlon';
import { PlaceResultWrapper } from './place-result-wrapper';
import { ACTIVITY_FILTERS, ActivityType } from '../search/filters/activity-filters';
import { convert, METERS, NAUTICAL_MILES } from '../util/units';
import { BoundingBox } from '../util/geodesy/bounding-box';
import { MAX_BBOX_AREA } from '../airport/airport.service';


/**
 * Promise-based wrapper for Google Maps/Places API.
 */
@Injectable()
export class GoogleService {
  private _loaded = false;
  private _loading = false;
  private _promise: Promise<void> = null;
  private _resolve: () => void;
  private _reject: (error: any) => void;
  private _attributionEl: HTMLDivElement;
  private _places: PlacesService;
  private _autocomplete: AutocompleteService;
  private _geocoder: Geocoder;

  /**
   * Returns a promise that resolves when the Google service has successfully loaded, or rejected if an error
   * occurred during initialization.
   * @returns {Promise<void>}
   */
  get promise(): Promise<void> {
    return this._promise;
  }

  constructor(private _loader: MapsAPILoader, private _errorHandler: ErrorHandler) {
    this._promise = new Promise<void>((resolve, reject) => {
      this._resolve = resolve;
      this._reject = reject;
    });
  }

  /**
   * Returns the icon URL to use for the given place result.
   * @param result {google.maps.places.PlaceResult} Google place result
   * @param [selectedType] {string} (Optional) If passed in, the icon type will be biased towards the
   *  given place type.
   * @returns {string} Icon type.
   */
  static getIconUrl(result: PlaceResult, selectedType?: string): string {
    const type = GoogleService.getIconType(result, selectedType);
    return type ? `assets/icons/${type}.png` : null;
  }

  /**
   * Returns the appropriate icon type to use for the given place result.
   * @param result {google.maps.places.PlaceResult} Google place result
   * @param [selectedType] {string} (Optional) If passed in, the icon type will be biased towards the
   *  given place type.
   * @returns {string} Icon type.
   */
  static getIconType(result: PlaceResult, selectedType?: string): string {
    // if it is a real Google type being selected, mark all results with that type
    if (selectedType
      && ['restaurant', 'park', 'museum', 'lodging'].includes(selectedType)
      && result.types.includes(selectedType)) {
      return selectedType;
    }
    // otherwise use the icon for that type in the following order
    if (result.types.includes('park')) {
      return 'park';
    }
    if (result.types.includes('restaurant')) {
      return 'restaurant';
    }
    if (result.types.includes('lodging')) {
      return 'lodging';
    }
    if (result.types.includes('car_rental') || result.types.includes('bus_station')
      || result.types.includes('subway_station') || result.types.includes('taxi_stand')
      || result.types.includes('train_station') || result.types.includes('transit_station')) {
      return 'taxi';
    }
    if (result.types.includes('museum')) {
      return 'museum';
    }
    return 'attraction';
  }

  async init(attributionEl: HTMLDivElement): Promise<void> {
    this._attributionEl = attributionEl;
    await this._load();
  }

  private async _load() {
    if (!this._loaded && !this._loading) {
      this._loading = true;
      try {
        await this._loader.load();
        this._places = new google.maps.places.PlacesService(this._attributionEl);
        this._autocomplete = new google.maps.places.AutocompleteService();
        this._geocoder = new google.maps.Geocoder();
        this._loaded = true;
        this._resolve();
      } catch (e) {
        this._errorHandler.handleError(e);
        this._reject(e);
        throw e;
      } finally {
        this._loading = false;
      }
    }
  }

  /**
   * Retrieves place autocomplete predictions based on the supplied autocomplete request.
   * @param {google.maps.places.AutocompletionRequest} query
   * @returns {Promise<google.maps.places.AutocompletePrediction[]>}
   */
  async getPlacePredictions(query: AutocompletionRequest): Promise<AutocompletePrediction[]> {
    await this._promise;
    return new Promise<AutocompletePrediction[]>((resolve, reject) => {
      try {
        this._autocomplete.getPlacePredictions(query,
          (results: AutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              return resolve(results);
            }
            if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              return resolve([]);
            }
            reject(new Error(status as any as string));
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Retrieves query autocomplete predictions based on the supplied query autocomplete request.
   * @param {google.maps.places.QueryAutocompletionRequest} query
   * @returns {Promise<google.maps.places.QueryAutocompletePrediction[]>}
   */
  async getQueryPredictions(query: QueryAutocompletionRequest): Promise<QueryAutocompletePrediction[]> {
    await this._promise;
    return new Promise<QueryAutocompletePrediction[]>((resolve, reject) => {
      try {
        this._autocomplete.getQueryPredictions(query,
          (results: QueryAutocompletePrediction[], status: google.maps.places.PlacesServiceStatus) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              return resolve(results);
            }
            if (status === google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
              return resolve([]);
            }
            reject(new Error(status as any as string));
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Geocode a request.
   * @param {google.maps.GeocoderRequest} request
   * @returns {Promise<google.maps.GeocoderResult[]>}
   */
  async geocode(request: GeocoderRequest): Promise<GeocoderResult[]> {
    await this._promise;
    return new Promise<GeocoderResult[]>((resolve, reject) => {
      try {
        this._geocoder.geocode(request,
          (results: GeocoderResult[], status: google.maps.GeocoderStatus) => {
            if (status === google.maps.GeocoderStatus.OK) {
              return resolve(results);
            }
            if (status === google.maps.GeocoderStatus.ZERO_RESULTS) {
              return resolve([]);
            }
            reject(new Error(status as any as string));
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Geocode a place ID, returning the LatLon (or null if geocoding was not successful).
   * @param {string} placeId Google place ID
   * @returns {Promise<LatLon>} LatLon (if geocoding was successful), or null (if no results)
   */
  async geocodePlace(placeId: string): Promise<LatLon> {
    await this._promise;
    const results = await this.geocode({ placeId });
    if (results.length > 0) {
      const result = results[0];
      const location = result.geometry.location;
      return new LatLon(location.lat(), location.lng());
    }
    return null;
  }

  /**
   * Retrieves a list of places near a particular location, based on keyword or type. Location must always be specified,
   * either by passing a LatLngBounds, or location and radius parameters. The PlaceResults returned from the function
   * are subsets of the full PlaceResult. Your can get a more detailed PlaceResult for each place by sending a Place
   * Details request passing the reference value for the desired place. The PlaceSearchPagination object can be used to
   * fetch additional pages of results (null if this is the last page of results or if there is only one page of
   * results).
   * @param {google.maps.places.PlaceSearchRequest} request
   * @returns {Promise<PlaceResultWrapper>}
   */
  async nearbySearch(request: PlaceSearchRequest): Promise<PlaceResultWrapper> {
    await this._promise;
    return await PlaceResultWrapper.wrap(callback => {
      this._places.nearbySearch(request, callback);
    });
  }

  /**
   * Retrieves the details for a Google Place.
   * @param {google.maps.places.PlaceDetailsRequest} request
   * @returns {Promise<PlaceResult>}
   */
  async placeDetails(request: PlaceDetailsRequest): Promise<PlaceResult> {
    await this._promise;
    return new Promise<PlaceResult>((resolve, reject) => {
      try {
        this._places.getDetails(request,
          (results: PlaceResult, status: google.maps.places.PlacesServiceStatus) => {
            if (status === google.maps.places.PlacesServiceStatus.OK) {
              return resolve(results);
            }
            reject(new Error(status as any as string));
          });
      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Performs a search for activities of the specified type within a radius of a lat/lon.
   * @param type {ActivityType} Activity type
   * @param location {LatLon} Center point for the search
   * @param radius {number} Radius in nautical miles
   * @returns {Promise<PlaceResultWrapper>} Paginated search results
   */
  async searchActivitiesByLatLon(type: ActivityType, location: LatLon, radius: number): Promise<PlaceResultWrapper> {
    await this._promise;
    const request: PlaceSearchRequest = {
      location: { lat: location.lat, lng: location.lon },
      radius: convert(radius, NAUTICAL_MILES, METERS)
    };
    this._populateActivityRequest(type, request);
    return await this.nearbySearch(request);
  }

  /**
   * Performs a search for activities of the specified type within a bounding box.
   * @param type {ActivityType} Activity type
   * @param bounds {BoundingBox} Bounding box search area
   * @returns {Promise<PlaceResultWrapper>} Paginated search results
   */
  async searchActivitiesByBoundingBox(type: ActivityType, bounds: BoundingBox): Promise<PlaceResultWrapper> {
    await this._promise;
    const area = bounds.getAreaDegrees();
    if (area > MAX_BBOX_AREA) {
      throw new Error(`Bounding box area (${area} square degrees) exceeds maximum (${MAX_BBOX_AREA}).`);
    }
    const ne = { lat: bounds.ne.lat, lng: bounds.ne.lon };
    const sw = { lat: bounds.sw.lat, lng: bounds.sw.lon };
    const request: PlaceSearchRequest = {
      bounds: new google.maps.LatLngBounds(sw, ne)
    };
    this._populateActivityRequest(type, request);
    return await this.nearbySearch(request);
  }

  /**
   * Populates the given PlaceSearchRequest instance with the details of an activity request
   * @param {ActivityType} type Activity type being searched for
   * @param {google.maps.places.PlaceSearchRequest} request
   * @private
   */
  private _populateActivityRequest(type: ActivityType, request: PlaceSearchRequest) {
    const activity = ACTIVITY_FILTERS.get(type);
    if (!activity) {
      throw new Error(`Unable to perform activity search: Activity filter with key ${type} is missing.`);
    }
    if (activity.placeType) {
      request.type = activity.placeType;
    } else if (activity.keyword) {
      request.keyword = activity.keyword;
    } else {
      throw new Error(`Activity filter with key ${activity.key} does not have placeType or keyword set. ` +
        `One or the other is required in order to perform an activity search.`);
    }
  }
}
